import { IAccOpening, IBeneficiarySaveParam, ILegalPersonSaveParam, ILicenseSaveParam } from '../../src/components';
import { ICompanyWallet, IPAccountApplySubmit, IPaymentAccount, IPaymentAccountParam, IPaymentCreateMeta, IUnSignDetail, IUnSignQuery } from '../../types/air-splitting/paymentAccount';
import { ECompanyType, EOpenBankType, ESplitAccountType } from '../../types/common';
import { ELianLianAccountStatus, ILianDetail, ILztStore } from '../../types/lianlian';
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getFileExt, getHostbaseUrl, isNull } from '@core/tools';
import { getAccOpeningSaveParam } from '../lianlian-utils';
import { IPersonalApplySubmit } from '../../types/air-splitting/paymentAccount';

const hostUrl = getHostbaseUrl();

/**
 * 空中分账 - 分账账户 - 查询免密支付授权url
 */
export async function queryPaymentAuthUrl(lianId: number) {
  let data = {} as { gateway_url: string };

  try {
    const { code, context, message } = await Http.post<{ gateway_url: string }>(`${hostUrl}/pay/payer/sign/pap-agree`, { lianId });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      data = context || {} as { gateway_url: string };
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 空中分账 - 分账账户 - 查询账户详细信息
 */
export async function queryPaymentDetail(lianId: number) {
  let data = {} as ILianDetail;

  try {
    const { code, context, message } = await Http.get<any>(`${hostUrl}/pay/payer/lian/detail`, { lianId });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      data = context || {} as ILianDetail;
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 空中分账 - 分账账户详情查询
 */
export async function queryPaymentAccDetail() {
  let data = [] as IPaymentAccount[];

  try {
    const { code, context, message } = await Http.get<{ data: IPaymentAccount[]; total: number }>(`${hostUrl}/pay/payer/page`);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items } = context || {} as { data: IPaymentAccount[]; total: number };

      data = resolvePaymentAccounts(items || []);
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 空中分账 - 分账账户查询
 */
export async function queryPaymentAccounts(param: IPaymentAccountParam) {
  let data = [] as ICompanyWallet[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IPaymentAccountParam;

  try {
    const { code, context, message } = await Http.get<{ data: ICompanyWallet[]; total: number }>(`${hostUrl}/pay/payer/page-new`, param);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: ICompanyWallet[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 空中分账 - 分账账户 - 启用或禁用
 */
export async function changePaymentAccountStatus(payerId: number, status: ELianLianAccountStatus) {
  try {
    const { code, message } = await Http.post<{ data: IPaymentAccount[]; total: number }>(`${hostUrl}/pay/payer/change-status`, {
      payerId,
      status,
    });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 分账账户 - 解冻冻结金额
 */
export async function unFrozenPaymentAccount() {
  try {
    const { code, message } = await Http.post<{ data: IPaymentAccount[]; total: number }>(`${hostUrl}/iam/un-frozen-payment-account`, {});

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 分账账户 - 新建之前的检查
 */
export async function checkPaymentAccount() {
  let data: IPaymentCreateMeta[] = [];

  try {
    const { code, context, message } = await Http.get<IPaymentCreateMeta[]>(`${hostUrl}/pay/payer/lian/get-channel-config`, {});

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      data = context || [];
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 空中分账 - 分账账户 - 获取来账通重新申请url
 */
export async function checkLztReApplyUrl(lianId: number) {
  let url = '';

  try {
    const { code, context, message } = await Http.post<{ gatewayUrl: string }>(`${hostUrl}/pay/payer/lian/lgz-open-acct`, { lianId });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      url = (context || {}).gatewayUrl || '';
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return url;
}

/**
 * 空中分账 - 分账账户 - 新建弹框点击下一步之前的检查
 */
export async function checkPaymentBank(openBank: EOpenBankType) {
  try {
    const { code, message } = await Http.get<IPaymentCreateMeta>(`${hostUrl}/pay/payer/lian/check`, { openBank });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 分账账户 - 上传营业执照信息保存
 */
export async function submitPayerLicense(
  param: ILicenseSaveParam,
  opts: { configId?: number; type?: ECompanyType; openBank?: EOpenBankType },
) {
  let lianId = '';

  param = JSON.parse(JSON.stringify(param)) as ILicenseSaveParam;

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/pay/payer/lian/save-business-info`,
      {
        ...param,
        type: opts?.type,
        openBank: opts.openBank,
        companyChannelConfigId: opts.configId,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      lianId = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return Number(lianId);
}

/**
 * 空中分账 - 分账账户 - 法人信息保存
 */
export async function submitPayerLegalPerson(param: ILegalPersonSaveParam) {
  param = JSON.parse(JSON.stringify(param)) as ILegalPersonSaveParam;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/payer/lian/save-legalrept-info`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 分账账户 - 受益人信息保存
 */
export async function submitPayerBeneficiary(param: IBeneficiarySaveParam) {
  param = JSON.parse(JSON.stringify(param)) as IBeneficiarySaveParam;

  param.uboList.forEach((v) => {
    if (v.idNoFrontUrl) {
      v.idNoFrontType = getFileExt(v.idNoFrontUrl);
      v.idNoBackType = getFileExt(v.idNoBackUrl);
    }
  });

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/payer/lian/save-ubo-info`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 分账账户 - 保存来账通信息保存
 */
export async function submitPayerLztStore(lztList: ILztStore[], lianId: number, lianNo?: string) {
  lztList = JSON.parse(JSON.stringify(lztList)) as ILztStore[];

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/payer/lian/save-lzt-info`,
      {
        lztList,
        lianId,
        lianNo: lianNo || undefined,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 申请账户 - 开户绑卡信息保存
 */
export async function submitPayerOpenparam(param: IAccOpening) {
  param = JSON.parse(JSON.stringify(param)) as IAccOpening;
  const p = getAccOpeningSaveParam(param as IAccOpening);

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/payer/lian/save-linked-acct-info`,
      p,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 申请分账账户 - 提交
 */
export async function submitPAccountInfo(param: IPAccountApplySubmit) {
  let token = '';

  param = JSON.parse(JSON.stringify(param)) as IPAccountApplySubmit;

  try {
    const { code, context, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/payer/lian/open-acct`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      token = (context || {}).token || '';
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return token;
}

/**
 * 空中分账 - 申请分账账户 - 最终提交
 */
export async function submitPayerPersonalAccount(param: IPersonalApplySubmit) {
  param = JSON.parse(JSON.stringify(param)) as IPersonalApplySubmit;

  try {
    const { code, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/payer/lian/open-acct-verify`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 分账账户 - 提交时候发送验证短信
 */
export async function sendPASmscode(regPhone: string, lianId: number) {
  try {
    const { code, message } = await Http.post<string>(
      `${hostUrl}/pay/payer/lian/phone-verify-code-apply`,
      {
        lianId,
        regPhone,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 待签署协议查询
 */
export async function queryUnSignList(param: IUnSignQuery) {
  let data = [] as IUnSignDetail[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IUnSignQuery;

  try {
    const { code, context, message } = await Http.post<{ data: IUnSignDetail[]; total: number }>(
      `${hostUrl}/pay/yop/company/page-query-agreement-signing`,
      param,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IUnSignDetail[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

function resolvePaymentAccounts(data: IPaymentAccount[]) {
  data.forEach((v) => {
    resolvePaymentAccount(v);
  });

  return data;
}

export function resolvePaymentAccount(v: IPaymentAccount) {
  const { status } = v;
  const enabled = status === ELianLianAccountStatus.Enabled;
  const enableOrDisable = [ELianLianAccountStatus.Enabled, ELianLianAccountStatus.Disabled].includes(status);

  v.showSplitBill = enabled;
  v.showWithdraw = enabled;
  v.showManage = enableOrDisable || status === ELianLianAccountStatus.LoggedOut;
  v.showEditApply = v.status === ELianLianAccountStatus.UnderApplying;
  v.showReApply = v.status === ELianLianAccountStatus.ApplyFailed;
  v.showNoPassAuth = [ELianLianAccountStatus.UnderNonPassAuth, ELianLianAccountStatus.Enabled].includes(v.status);
  v.showNoPassReAuth = v.status === ELianLianAccountStatus.NonPassAuthOutDate;
  v.showVerify = v.status === ELianLianAccountStatus.UnderVerifyed;

  if (!enableOrDisable) {
    v.enableOrDisable = undefined;
  } else {
    v.enableOrDisable = ELianLianAccountStatus.Enabled === status;
  }

  v.totalAmount = !isNull(v.totalAmount) ? v.totalAmount : '';
  v.availableBalance = !isNull(v.availableBalance) ? v.availableBalance : '';
  v.payingBalance = !isNull(v.payingBalance) ? v.payingBalance : '';
  v.frozenBalance = !isNull(v.frozenBalance) ? v.frozenBalance : '';
  v.settleServiceRate = !isNull(v.settleServiceRate) ? v.settleServiceRate : '';

  if (v.status === ELianLianAccountStatus.UnderVerifyed) {
    v.remark = '请进行短信验证';
  }

  return v;
}

/**
 * 空中分账 - 分账账户 - 查询提交分账可用的支付通道
 */
// export async function queryUsageChannels(splitOrderModel?: ESplitAccountType) {
//   let data: { channelId: number; channelName: string }[] = [];

//   try {
//     const { code, context, message } = await Http.get<{channelId: number;channelName: string}[]>(`${hostUrl}/pay/payer/usage-channels`, { splitOrderModel });

//     if (code !== EAxiosResponseCode.Succeed) {
//       throw new Error(message || '服务器异常，请稍后再试~');
//     } else {
//       data = context || [];
//     }
//   } catch (error) {
//     throw new Error(getHttpErrorMessage(error));
//   }

//   return v;
// }

/**
 * 空中分账 - 分账账户 - 查询提交分账可用的支付通道
 */
export async function queryUsageChannels(splitOrderModel?: ESplitAccountType, companyId?: number) {
  let data: { channelId: number; channelName: string }[] = [];

  try {
    const { code, context, message } = await Http.get<{channelId: number;channelName: string}[]>(`${hostUrl}/pay/payer/usage-channels`, {
      splitOrderModel,
      companyId, 
    });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      data = context || [];
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}
